import { SUPPLIER_BACKEND_API_ENDPOINTS } from "../config/apiConfig";
import { apiClient } from "./api-client.service";
import type { ApiResponse } from "./api-client.service";

import type { CreateSupplierPayload } from "../types/signup.types";
import type { CountryResponse } from "../types/countries.types";
import type { StateResponse } from "../types/states.types";
import type { CityApiResponse } from "../types/cities.types";
import type { UploadFileResponse } from "../types/common.types";

const endpoints = SUPPLIER_BACKEND_API_ENDPOINTS;

const failure = (error: unknown, fallback: string) => ({
  success: false,
  statusCode: 500,
  message: error instanceof Error ? error.message : fallback,
  resData: null,
});

const toQuery = (filter: Record<string, string | number | undefined>) => {
  const params = new URLSearchParams();
  Object.entries(filter).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "") {
      params.set(key, String(value));
    }
  });
  return params.toString();
};

// Signup is used before login, so none of these calls send an auth token.
export const signupService = {
  async countries(): Promise<CountryResponse> {
    try {
      return await apiClient<CountryResponse>(
        endpoints.countries.list(toQuery({ page: 1, status: "active" })),
        { method: "GET", cache: "no-store" },
      );
    } catch (error) {
      return failure(error, "Failed to load countries.") as unknown as CountryResponse;
    }
  },

  async states(countryId: number | string): Promise<StateResponse> {
    try {
      return await apiClient<StateResponse>(
        endpoints.states.list(toQuery({ page: 1, status: "active", country_id: countryId })),
        { method: "GET", cache: "no-store" },
      );
    } catch (error) {
      return failure(error, "Failed to load states.") as unknown as StateResponse;
    }
  },

  async cities(stateId: number | string): Promise<CityApiResponse> {
    try {
      return await apiClient<CityApiResponse>(
        endpoints.cities.list(toQuery({ page: 1, status: "active", state_id: stateId })),
        { method: "GET", cache: "no-store" },
      );
    } catch (error) {
      return failure(error, "Failed to load cities.") as unknown as CityApiResponse;
    }
  },

  async uploadFile(file: File): Promise<UploadFileResponse> {
    try {
      const formData = new FormData();
      formData.append("docs", file);
      return await apiClient<UploadFileResponse>(endpoints.common.uploadFile, {
        method: "POST",
        body: formData,
      });
    } catch (error) {
      return failure(error, "Failed to upload file.") as unknown as UploadFileResponse;
    }
  },

  async createSupplier(payload: CreateSupplierPayload): Promise<ApiResponse<unknown>> {
    try {
      return await apiClient<ApiResponse<unknown>>(endpoints.suppliers.create, {
        method: "POST",
        body: JSON.stringify(payload),
      });
    } catch (error) {
      return failure(error, "Failed to create supplier.") as unknown as ApiResponse<unknown>;
    }
  },
};
